// Local Matchday Types
export interface LocalMatchday {
  id: number;
  name: string;
  location?: string;
  description?: string;
  date: string;
  startTime?: string;
  endTime?: string;
  organizer?: string;
  expectedAttendees?: number;
  actualAttendees?: number;
  notes?: string;
  isActive: boolean;
  isCompleted: boolean;
  createdAt: string;
  updatedAt: string;
  matchCount: number;
  status: string;
}

export interface CreateLocalMatchdayRequest {
  name: string;
  location?: string;
  description?: string;
  date: string;
  startTime?: string;
  endTime?: string;
  organizer?: string;
  expectedAttendees?: number;
  notes?: string;
  isActive?: boolean;
}

export interface UpdateLocalMatchdayRequest {
  name: string;
  location?: string;
  description?: string;
  date: string;
  startTime?: string;
  endTime?: string;
  organizer?: string;
  expectedAttendees?: number;
  actualAttendees?: number;
  notes?: string;
  isActive?: boolean;
  isCompleted?: boolean;
}

// Local Match Types
export interface LocalMatch {
  id: number;
  homeTeam: string;
  awayTeam: string;
  homeScore?: number;
  awayScore?: number;
  scheduledDate: string;
  actualDate?: string;
  location?: string;
  referee?: string;
  duration?: number;
  notes?: string;
  status: LocalMatchStatus | string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  localMatchdayId: number;
  result: string;
  winner: string;
  localMatchday: LocalMatchdayInfo;
}

export interface CreateLocalMatchRequest {
  homeTeam: string;
  awayTeam: string;
  scheduledDate: string;
  actualDate?: string;
  location?: string;
  referee?: string;
  duration?: number;
  notes?: string;
  status?: LocalMatchStatus;
  localMatchdayId: number;
  homeScore?: number;
  awayScore?: number;
  isActive?: boolean;
}

export interface UpdateLocalMatchRequest {
  homeTeam: string;
  awayTeam: string;
  scheduledDate: string;
  actualDate?: string;
  location?: string;
  referee?: string;
  duration?: number;
  notes?: string;
  status?: LocalMatchStatus;
  homeScore?: number;
  awayScore?: number;
  isActive?: boolean;
}

export interface LocalMatchdayInfo {
  id: number;
  name: string;
  date: string;
  location?: string;
  organizer?: string;
}

export interface LocalMatchStatusInfo {
  value: LocalMatchStatus | string;
  label: string;
  color: string;
  icon: string;
}

export enum LocalMatchStatus {
  Scheduled = 0,
  Live = 1,
  Finished = 2,
  Postponed = 3,
  Cancelled = 4
}

// Helper functions
export const getLocalMatchStatusInfo = (status: LocalMatchStatus | string): LocalMatchStatusInfo => {
  // Handle both numeric enum values and string values from API
  const statusMap: Record<string, LocalMatchStatusInfo> = {
    [LocalMatchStatus.Scheduled]: { value: LocalMatchStatus.Scheduled, label: 'Geplant', color: '#6c757d', icon: '' },
    [LocalMatchStatus.Live]: { value: LocalMatchStatus.Live, label: 'Live', color: '#28a745', icon: '' },
    [LocalMatchStatus.Finished]: { value: LocalMatchStatus.Finished, label: 'Beendet', color: '#007bff', icon: '' },
    [LocalMatchStatus.Postponed]: { value: LocalMatchStatus.Postponed, label: 'Verschoben', color: '#ffc107', icon: '' },
    [LocalMatchStatus.Cancelled]: { value: LocalMatchStatus.Cancelled, label: 'Abgesagt', color: '#dc3545', icon: '' },
    // String values from API
    'Scheduled': { value: LocalMatchStatus.Scheduled, label: 'Geplant', color: '#6c757d', icon: '' },
    'Live': { value: LocalMatchStatus.Live, label: 'Live', color: '#28a745', icon: '' },
    'Finished': { value: LocalMatchStatus.Finished, label: 'Beendet', color: '#007bff', icon: '' },
    'Postponed': { value: LocalMatchStatus.Postponed, label: 'Verschoben', color: '#ffc107', icon: '' },
    'Cancelled': { value: LocalMatchStatus.Cancelled, label: 'Abgesagt', color: '#dc3545', icon: '' }
  };
  const result = statusMap[status];
  return result || { value: LocalMatchStatus.Scheduled, label: 'Unbekannt', color: '#6c757d', icon: '' };
};

export const getLocalMatchdayStatusInfo = (status: string) => {
  const statusMap: Record<string, { label: string; color: string; icon: string }> = {
    'planned': { label: 'Geplant', color: '#6c757d', icon: '' },
    'ongoing': { label: 'Läuft', color: '#28a745', icon: '' },
    'completed': { label: 'Abgeschlossen', color: '#007bff', icon: '' },
    'cancelled': { label: 'Abgesagt', color: '#dc3545', icon: '' }
  };
  return statusMap[status] || { label: status, color: '#6c757d', icon: '' };
};

export const formatLocalMatchResult = (match: LocalMatch): string => {
  if (match.homeScore !== null && match.homeScore !== undefined && 
      match.awayScore !== null && match.awayScore !== undefined) {
    return `${match.homeScore}:${match.awayScore}`;
  }
  return '-:-';
};

export const formatLocalMatchTeams = (match: LocalMatch): string => {
  return `${match.homeTeam} vs ${match.awayTeam}`;
};

// Import unified DateTime utilities
import {
  formatDateTime,
  formatDate,
  formatTime,
  isToday
} from '../utils/dateTimeUtils';

// Re-export for backward compatibility
export { formatDateTime, formatDate, formatTime };

export const isLocalMatchdayToday = (matchday: LocalMatchday): boolean => {
  return isToday(matchday.date);
};

export const isLocalMatchdayUpcoming = (matchday: LocalMatchday): boolean => {
  const today = new Date();
  const matchdayDate = new Date(matchday.date);
  return matchdayDate > today;
};

export const isLocalMatchdayPast = (matchday: LocalMatchday): boolean => {
  const today = new Date();
  const matchdayDate = new Date(matchday.date);
  return matchdayDate < today && !isLocalMatchdayToday(matchday);
};
